import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Form, Input, Select, Button, Row, Col, Card, Spin } from "antd";
import { debounce } from "lodash";
import {
  formatAndExtractCountryCode,
  handleMobileChange,
  prefixSelector,
  validateMobileNumber,
} from "../../utils/mobileValidation";
import { useCreateEditUser } from "./hooks/useCreateEditUser";
import { searchTeams } from "../../actionCreator/teamManagementActionCreator";
import { searchRoles } from "../../actionCreator/roleManagementActionCreator";

const { Option } = Select;

const CreateUserForm = ({ initialValues, closeModal }) => {
  const dispatch = useDispatch();
  const [isDisable, setDisable] = useState(false);
  const [teams, setTeams] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [roles, setRoles] = useState([]);
  const [roleFetching, setRoleFetching] = useState(false);
  const [form] = Form.useForm();

  const {
    handleCreateUpdateUser,
    error,
    loading,
    setError,
    isTeamChange,
    setTeamChange,
  } = useCreateEditUser();

  useEffect(() => {
    if (initialValues?.id) {
      form.setFieldsValue({
        ...initialValues,
        prefix: formatAndExtractCountryCode(initialValues?.mobileNo)
          ?.countryLabel,
        mobileNo: formatAndExtractCountryCode(initialValues?.mobileNo)
          ?.numberWithoutCountryCode,
        fullName: initialValues?.firstName + " " + initialValues?.lastName,
      });
      setDisable(true);
    }
  }, [initialValues, form]);

  useEffect(() => {
    const fetchDefault = async () => {
      try {
        const res = await dispatch(searchTeams());
        setTeams(res?.content || []);
        const response = await dispatch(searchRoles());
        setRoles(response?.roles || []);
      } catch (error) {}
    };

    fetchDefault();
  }, [dispatch]);

  const fetchOptions = debounce(async (value) => {
    setFetching(true);
    try {
      const res = await dispatch(searchTeams(value));
      setTeams(res?.content || []);
    } catch (err) {}
    setFetching(false);
  }, 500);

  const fetchRoleOptions = debounce(async (value) => {
    setRoleFetching(true);
    try {
      const res = await dispatch(searchRoles(value));
      setRoles(res?.roles || []);
    } catch (err) {}
    setRoleFetching(false);
  }, 500);

  return (
    <Card
      title={
        <div className="text-theme heading18px">
          {initialValues?.id ? "Edit User" : "Create New User"}
        </div>
      }
      className="rounded-2xl shadow-md"
    >
      <Form
        layout="vertical"
        form={form}
        initialValues={{ prefix: "India" }}
        onFinish={(val) =>
          handleCreateUpdateUser(
            isTeamChange ? { ...val, confirmTeamChange: isTeamChange } : val,
            initialValues,
            () => {
              form.resetFields();
              closeModal && closeModal();
              setTeamChange(false);
            }
          )
        }
        onValuesChange={(changedValues, allValues) => {
          setError();
          const keysToTrack = [
            "fullName",
            "email",
            "mobileNo",
            "prefix",
            "role",
            "team",
          ];
          const initValues = {
            ...initialValues,
            prefix: formatAndExtractCountryCode(initialValues?.mobileNo)
              ?.countryLabel,
            mobileNo: formatAndExtractCountryCode(initialValues?.mobileNo)
              ?.numberWithoutCountryCode,
            fullName: initialValues?.firstName + " " + initialValues?.lastName,
          };

          const hasChanges = keysToTrack.some(
            (key) =>
              (initValues[key] || "").trim() !== (allValues[key] || "").trim()
          );
          setDisable(!hasChanges);
        }}
        autoComplete="off"
      >
        {error && <p className="text-error text-center pb-2">{error}</p>}
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              label={<div className="fw-medium">Full Name</div>}
              name="fullName"
              rules={[{ required: true, message: "Please enter full name" }]}
            >
              <Input placeholder="Enter full name" />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item
              label={<div className="fw-medium">Email</div>}
              name="email"
              rules={[
                { required: true, message: "Please enter email" },
                { type: "email", message: "Enter a valid email" },
              ]}
            >
              <Input placeholder="Enter email address" />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item
              label={<div className="fw-medium">Mobile Number</div>}
              name="mobileNo"
              rules={[
                { required: true, message: "Please enter mobile number" },
                {
                  validator: validateMobileNumber,
                },
              ]}
            >
              <Input
                placeholder="Enter mobile number"
                className="mobile-no"
                addonBefore={prefixSelector()}
                maxLength={20}
                onChange={(e) => handleMobileChange(e.target.value, form)}
              />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item
              label={<div className="fw-medium">Role</div>}
              name="role"
              rules={[{ required: true, message: "Please select a role" }]}
            >
              <Select
                showSearch
                placeholder="Search role..."
                onSearch={fetchRoleOptions}
                filterOption={false}
                notFoundContent={roleFetching ? <Spin size="small" /> : null}
                allowClear
              >
                {roles
                  ?.filter((d) => d?.name !== "ADMIN")
                  .map((item) => (
                    <Option key={item.id} value={item.name}>
                      {item.name}
                    </Option>
                  ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              label={<div className="fw-medium">Team/Hotel</div>}
              name="team"
            >
              <Select
                showSearch
                placeholder="Search team..."
                onSearch={fetchOptions}
                filterOption={false}
                notFoundContent={fetching ? <Spin size="small" /> : null}
                allowClear
              >
                {teams.map((item) => (
                  <Option key={item.id} value={item.teamName}>
                    {item.teamName}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          {isTeamChange && (
            <Col span={24}>
              <div className="fw-semibold">
                Are you sure you want to change the team?
              </div>
              <div className="d-flex align-items-center justify-content-end gap-20 mt20">
                {!!initialValues?.id && (
                  <Button
                    disabled={loading}
                    className="secondary-btn"
                    onClick={() => {
                      closeModal();
                      setTeamChange(false);
                    }}
                  >
                    No
                  </Button>
                )}
                <Button
                  disabled={loading || isDisable}
                  type="primary"
                  className="theme-btn"
                  htmlType="submit"
                >
                  Yes
                </Button>
              </div>
            </Col>
          )}
        </Row>
        {!isTeamChange && (
          <div className="d-flex align-items-center justify-content-end gap-20">
            {!!initialValues?.id && (
              <Button
                disabled={loading}
                className="secondary-btn"
                onClick={() => {
                  closeModal();
                  setTeamChange(false);
                }}
              >
                Cancel
              </Button>
            )}
            <Button
              disabled={loading || isDisable}
              type="primary"
              className={`theme-btn ${!initialValues?.id && "w-100"}`}
              htmlType="submit"
            >
              {initialValues?.id ? "Update User" : "Create User"}
            </Button>
          </div>
        )}
      </Form>
    </Card>
  );
};

export default CreateUserForm;
